import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Table, Spin, Input, AutoComplete, Select } from "antd";
import {
  ReloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  RiseOutlined,
  WarningOutlined,
  SearchOutlined,
  GlobalOutlined,
  AppstoreOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import ReactECharts from "echarts-for-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import styled, { keyframes, createGlobalStyle } from "styled-components";

// ── LIVE PostHog data via the query API ─────────────────────────────────────
const POSTHOG_HOST = "https://us.posthog.com";
const POSTHOG_PROJECT_ID = "471193";
// SCOPED, READ-ONLY key (query:read). Set in public/index.html:
//   window.__POSTHOG_QUERY_KEY__ = "phx_...";  (never the full-access key)

const POSTHOG_QUERY_KEY = process.env.REACT_APP_POSTHOG_QUERY_KEY;

// Below this many distinct users in a country, adoption % is statistical noise
// (e.g. 1 user = 100% or 0%, never anything in between) — flag it, don't assert it.
const MIN_SAMPLE_USERS = 5;

// Rows can grow unbounded (more countries/modules over time) — keep the UI
// readable without silently hiding data: charts default to showing the top N
// but stay scrollable/zoomable to reach the rest, and the table paginates +
// is searchable rather than rendering everything at once.
const TABLE_PAGE_SIZE = 10;
const CHART_TOP_N = 12; // default visible window in charts, and the pie's slice cap
const CHART_SAFETY_CAP = 300; // hard ceiling so a pathological row count can't hang the browser

const POSTHOG_HOGQL = `WITH base AS (
  SELECT person_id, person.properties.country AS country, properties.$session_id AS session_id, timestamp,
    if(coalesce(properties.$pathname, '/') = '/', 'dashboard', arrayElement(splitByChar('/', trimLeft(coalesce(properties.$pathname, ''), '/')), 1)) AS module,
    leadInFrame(timestamp) OVER (PARTITION BY session_id ORDER BY timestamp ROWS BETWEEN CURRENT ROW AND 1 FOLLOWING) AS next_ts
  FROM events
  WHERE event = '$pageview' AND person.properties.country IS NOT NULL
),
durations AS (SELECT person_id, country, module, if(next_ts > timestamp, dateDiff('second', timestamp, next_ts), NULL) AS duration_seconds FROM base),
country_totals AS (SELECT country, count(DISTINCT person_id) AS total_users FROM durations GROUP BY country)
SELECT d.country AS country, d.module AS module, count(DISTINCT d.person_id) AS users, count() AS pageviews,
  round(sum(coalesce(d.duration_seconds, 0)) / 60.0, 1) AS total_minutes,
  round(avg(d.duration_seconds), 1) AS avg_seconds_per_view,
  round(100.0 * count(DISTINCT d.person_id) / t.total_users, 1) AS adoption_rate_pct,
  t.total_users AS country_total_users
FROM durations AS d JOIN country_totals AS t ON d.country = t.country
GROUP BY d.country, d.module, t.total_users
ORDER BY country ASC, total_minutes DESC`;

/* ── Fonts ──────────────────────────────────────────────────────────────── */
const Fonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=Archivo:wght@400;500;600&family=JetBrains+Mono:wght@400;500;700&display=swap');
`;

/* Theme variables also defined on :root (not just on the Page component).
   antd portals dropdowns/tooltips/modals straight to document.body, which
   sits OUTSIDE Page's DOM subtree — so var(--panel2) etc. used in any
   global style targeting those portaled elements would otherwise resolve
   to nothing. This is what caused the filter dropdown to render with a
   transparent background and bleed through the KPI cards behind it. */
const RootVars = createGlobalStyle`
  :root {
    --ink: #08090b;
    --panel: #0e1116;
    --panel2: #141821;
    --line: #20242e;
    --text: #eef1f4;
    --muted: #868d99;
    --accent: #c6f24e;
    --accent-dim: rgba(198, 242, 78, 0.12);
  }
`;

const fade = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Shell ──────────────────────────────────────────────────────────────── */
const Page = styled.div`
  --ink: #08090b;
  --panel: #0e1116;
  --panel2: #141821;
  --line: #20242e;
  --text: #eef1f4;
  --muted: #868d99;
  --accent: #c6f24e;
  --accent-dim: rgba(198, 242, 78, 0.12);

  min-height: 100vh;
  position: relative;
  background-color: var(--ink);
  background-image: linear-gradient(rgba(255, 255, 255, 0.022) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.022) 1px, transparent 1px);
  background-size: 46px 46px;
  color: var(--text);
  font-family: "Archivo", system-ui, sans-serif;
  padding: 48px 20px 72px;
  overflow-x: hidden;

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(
        920px 520px at 82% -12%,
        rgba(198, 242, 78, 0.1),
        transparent 60%
      ),
      radial-gradient(700px 520px at -10% 18%, rgba(90, 150, 255, 0.07), transparent 60%);
  }
  &::after {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    opacity: 0.04;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  }
`;

/* antd table, themed — a separate createGlobalStyle block. Nesting this CSS
   inside Page's own template literal caused stylis to silently drop the
   whole block (no idea why — confirmed via the injected stylesheet that
   these rules never made it out, regardless of &-prefixing). */
const TableTheme = createGlobalStyle`
  .ant-table,
  .ant-table-container,
  .ant-table-content,
  .ant-table-cell-fix-left,
  .ant-table-cell-fix-right,
  .ant-table-sticky-holder,
  .ant-table-sticky-scroll {
    background: transparent !important;
    color: var(--text);
    font-family: "Archivo", sans-serif;
  }
  .ant-table-thead > tr > th {
    background: var(--panel2) !important;
    color: var(--muted);
    border-bottom: 1px solid var(--line);
    font-family: "JetBrains Mono", monospace;
    font-size: 10.5px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }
  .ant-table-tbody > tr > td {
    background: transparent !important;
    color: var(--text);
    border-bottom: 1px solid var(--line);
  }
  .ant-table-tbody > tr.ant-table-row:hover > td { background: rgba(198, 242, 78, 0.05) !important; }
  .ant-table-placeholder,
  .ant-table-placeholder:hover > td,
  tr.ant-table-placeholder:hover > td {
    background: transparent !important;
  }
  .ant-empty-description { color: var(--muted); }
  .ant-spin-text { color: var(--accent); }
  .ant-spin-dot-item { background: var(--accent); }

  /* pagination — only shows once a table crosses the page-size threshold, so it
     was never themed before. The select dropdown renders in a body-level portal,
     not under Page, which is exactly why these rules need to be truly global. */
  .ant-pagination-total-text { color: var(--muted) !important; }
  .ant-pagination-item {
    background: transparent !important;
    border-color: var(--line) !important;
  }
  .ant-pagination-item a { color: var(--muted) !important; }
  .ant-pagination-item-active {
    border-color: var(--accent) !important;
    background: var(--accent-dim) !important;
  }
  .ant-pagination-item-active a { color: var(--accent) !important; }
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    background: transparent !important;
    border-color: var(--line) !important;
    color: var(--muted) !important;
  }
  .ant-pagination-disabled .ant-pagination-item-link { color: rgba(255, 255, 255, 0.2) !important; }
  .ant-pagination-options-size-changer.ant-select .ant-select-selector {
    background: var(--panel2) !important;
    border-color: var(--line) !important;
    color: var(--text) !important;
  }
  .ant-pagination-options-size-changer .ant-select-arrow { color: var(--muted) !important; }
  .ant-select-dropdown {
    background: var(--panel2) !important;
    border: 1px solid var(--line) !important;
  }
  .ant-select-item { color: var(--text) !important; }
  .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background: rgba(198, 242, 78, 0.08) !important;
  }
  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: var(--accent-dim) !important;
    color: var(--accent) !important;
  }

  /* general Select/AutoComplete selector box — covers the country/module
     filters, which render their own input through .ant-select-selector
     rather than a nested antd <Input>, so they need this themed directly. */
  .ant-select:not(.ant-pagination-options-size-changer) .ant-select-selector {
    background: var(--panel2) !important;
    border-color: var(--line) !important;
    color: var(--text) !important;
    height: 38px !important;
  }
  .ant-select:not(.ant-pagination-options-size-changer):hover .ant-select-selector {
    border-color: rgba(198, 242, 78, 0.4) !important;
  }
  .ant-select-focused:not(.ant-pagination-options-size-changer) .ant-select-selector {
    border-color: var(--accent) !important;
    box-shadow: 0 0 0 2px rgba(198, 242, 78, 0.15) !important;
  }
  .ant-select-selection-search-input {
    color: var(--text) !important;
  }
  .ant-select-selection-placeholder {
    color: var(--muted) !important;
  }
  .ant-select-clear {
    background: var(--panel2) !important;
    color: var(--muted) !important;
  }
  .ant-select-clear:hover { color: var(--text) !important; }

  /* search input above the table */
  .ant-input,
  .ant-input-affix-wrapper {
    background: var(--panel2) !important;
    border-color: var(--line) !important;
    color: var(--text) !important;
  }
  .ant-input-affix-wrapper .ant-input { background: transparent !important; }
  .ant-input::placeholder { color: var(--muted) !important; opacity: 1; }
  .ant-input-affix-wrapper:hover,
  .ant-input:hover { border-color: rgba(198, 242, 78, 0.4) !important; }
  .ant-input-affix-wrapper-focused,
  .ant-input-affix-wrapper:focus,
  .ant-input:focus {
    border-color: var(--accent) !important;
    box-shadow: 0 0 0 2px rgba(198, 242, 78, 0.15) !important;
  }
  .ant-input-clear-icon { color: var(--muted) !important; }
  .ant-input-clear-icon:hover { color: var(--text) !important; }

  /* searchable filter dropdowns (AutoComplete) — renders in a body-level
     portal like the pagination select, so it needs the same truly-global
     treatment rather than scoped styles under Page. Belt-and-suspenders:
     every ancestor layer gets a solid background AND each row gets its
     own solid background too, since antd's virtual-scroll wrapper can
     introduce extra unstyled positioning divs between the panel and the
     row that aren't worth chasing by exact class name. */
  .filter-autocomplete-dropdown,
  .filter-autocomplete-dropdown .ant-select-dropdown,
  .filter-autocomplete-dropdown .rc-virtual-list,
  .filter-autocomplete-dropdown .rc-virtual-list-holder,
  .filter-autocomplete-dropdown .rc-virtual-list-holder-inner,
  .filter-autocomplete-dropdown .rc-virtual-list-holder-inner > div {
    background-color: var(--panel2) !important;
  }
  .filter-autocomplete-dropdown {
    border: 1px solid var(--line) !important;
    border-radius: 10px !important;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45) !important;
    padding: 4px !important;
    opacity: 1 !important;
  }
  .filter-autocomplete-dropdown .ant-select-item {
    background-color: var(--panel2) !important;
    color: var(--text) !important;
    font-family: "Archivo", sans-serif;
    font-size: 13px;
    border-radius: 7px !important;
  }
  .filter-autocomplete-dropdown .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background-color: rgba(198, 242, 78, 0.14) !important;
  }
  .filter-autocomplete-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background-color: rgba(198, 242, 78, 0.12) !important;
    color: var(--accent) !important;
  }
  .filter-autocomplete-dropdown .ant-select-item-empty {
    background-color: var(--panel2) !important;
    color: var(--muted) !important;
  }
  .filter-autocomplete-dropdown mark {
    background: transparent !important;
    color: var(--accent) !important;
    font-weight: 600;
  }

  /* multi-select tags inside the module Select */
  .ant-select-multiple .ant-select-selection-item {
    background: var(--accent-dim) !important;
    border-color: rgba(198, 242, 78, 0.35) !important;
    color: var(--accent) !important;
    font-family: "JetBrains Mono", monospace;
    font-size: 11px;
    border-radius: 6px !important;
  }
  .ant-select-multiple .ant-select-selection-item-remove {
    color: var(--accent) !important;
  }
  .ant-select-multiple .ant-select-selection-item-remove:hover {
    color: var(--text) !important;
  }
  .ant-select-multiple .ant-select-selection-overflow {
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px 0;
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1160px;
  margin: 0 auto;
`;

const Reveal = styled.div`
  animation: ${fade} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

/* ── Hero ───────────────────────────────────────────────────────────────── */
const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 30px;
`;

const Eyebrow = styled.div`
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: var(--accent);
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;

  &::before {
    content: "";
    width: 26px;
    height: 1px;
    background: var(--accent);
  }
`;

const Headline = styled.h1`
  font-family: "Fraunces", serif;
  font-weight: 600;
  font-size: clamp(34px, 5vw, 58px);
  line-height: 0.98;
  letter-spacing: -0.02em;
  margin: 0;
  color: var(--text);

  em {
    font-style: italic;
    color: var(--accent);
  }
`;

const Sub = styled.p`
  margin: 14px 0 0;
  max-width: 540px;
  color: var(--muted);
  font-size: 14.5px;
  line-height: 1.55;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  font-family: "JetBrains Mono", monospace;
  font-size: 11.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease,
    opacity 0.15s ease;
  border: 1px solid
    ${(p) => (p.$primary ? "var(--accent)" : "var(--line)")};
  background: ${(p) => (p.$primary ? "var(--accent)" : "transparent")};
  color: ${(p) => (p.$primary ? "#0b0d08" : "var(--text)")};
  font-weight: ${(p) => (p.$primary ? 700 : 500)};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    border-color: var(--accent);
    ${(p) => (!p.$primary ? "color: var(--accent);" : "")}
  }
  &:disabled {
    opacity: 0.38;
    cursor: not-allowed;
  }
`;

/* ── KPIs ───────────────────────────────────────────────────────────────── */
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 18px;
`;

const Kpi = styled.div`
  position: relative;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 20px 20px 18px;
  overflow: hidden;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    top: 14px;
    right: 14px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 12px var(--accent);
    opacity: 0.5;
  }
  &:hover {
    border-color: rgba(198, 242, 78, 0.4);
    transform: translateY(-3px);
  }

  .label {
    font-family: "JetBrains Mono", monospace;
    font-size: 10.5px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .value {
    font-family: "Fraunces", serif;
    font-weight: 600;
    font-size: 42px;
    line-height: 1;
    margin-top: 12px;
    letter-spacing: -0.02em;
  }
  .foot {
    margin-top: 8px;
    font-size: 12px;
    color: var(--muted);
  }
`;

const Insight = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  background: linear-gradient(90deg, var(--accent-dim), transparent 70%);
  border: 1px solid var(--line);
  border-left: 3px solid var(--accent);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 30px;
  font-size: 15px;
  line-height: 1.5;

  .ico {
    color: var(--accent);
    margin-top: 2px;
  }
  b {
    color: var(--accent);
    font-weight: 600;
  }
`;

const Caveat = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 12.5px;
  color: #ffb454;
`;

/* ── Global filters (country + module, searchable w/ suggestions) ───────── */
const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-bottom: 18px;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;
  flex: 1 1 220px;
  max-width: 360px;
`;

const FilterLabel = styled.label`
  font-family: "JetBrains Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted);
  display: flex;
  align-items: center;
  gap: 6px;

  .ico { color: var(--accent); font-size: 11px; }
`;

/* Wraps an AutoComplete so a search icon can sit visibly inset inside the
   field. Deliberately NOT using antd's <Input prefix={...} /> pattern as
   AutoComplete's child — that nesting fights AutoComplete's own internal
   input box model across versions and was clipping the icon at the edge.
   Instead the icon is positioned independently on top, and the underlying
   input gets left padding to clear it. */
const SearchableField = styled.div`
  position: relative;

  .search-ico {
    position: absolute;
    top: 50%;
    left: 13px;
    transform: translateY(-50%);
    color: var(--muted);
    font-size: 14px;
    pointer-events: none;
    z-index: 2;
  }

  .ant-select-selector,
  input.ant-select-selection-search-input {
    padding-left: 34px !important;
  }
`;

const ClearAllBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid var(--line);
  color: var(--muted);
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  padding: 9px 12px;
  border-radius: 10px;
  cursor: pointer;
  height: 38px;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #ff6b81;
    color: #ff6b81;
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const ActiveFilters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: -6px 0 18px;
`;

const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: "JetBrains Mono", monospace;
  font-size: 11.5px;
  padding: 5px 6px 5px 12px;
  border-radius: 999px;
  background: var(--accent-dim);
  color: var(--accent);
  border: 1px solid rgba(198, 242, 78, 0.35);

  .x {
    display: inline-flex;
    cursor: pointer;
    opacity: 0.75;
    transition: opacity 0.15s ease;
  }
  .x:hover { opacity: 1; }
`;


const Panel = styled.section`
  position: relative;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 22px;
  margin-bottom: 22px;

  /* terminal corner ticks */
  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 12px;
    height: 12px;
    border-color: var(--accent);
    opacity: 0.5;
  }
  &::before {
    top: 10px;
    left: 10px;
    border-top: 1.5px solid;
    border-left: 1.5px solid;
  }
  &::after {
    bottom: 10px;
    right: 10px;
    border-bottom: 1.5px solid;
    border-right: 1.5px solid;
  }
`;

const PanelHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  .t {
    font-family: "JetBrains Mono", monospace;
    font-size: 12px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text);
  }
  .m {
    font-family: "JetBrains Mono", monospace;
    font-size: 11px;
    color: var(--muted);
  }
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: "JetBrains Mono", monospace;
  font-size: 11.5px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${(p) =>
    p.$warn ? "rgba(255,180,84,0.12)" : p.$accent ? "var(--accent-dim)" : "rgba(255,255,255,0.05)"};
  color: ${(p) => (p.$warn ? "#ffb454" : p.$accent ? "var(--accent)" : "var(--text)")};
  border: 1px solid ${(p) =>
    p.$warn ? "rgba(255,180,84,0.35)" : p.$accent ? "rgba(198,242,78,0.35)" : "var(--line)"};
`;

const Mono = styled.span`
  font-family: "JetBrains Mono", monospace;
  font-size: 13px;
`;

/* ── In-table data bars (Excel-style "data bars" so magnitude reads at a glance) ── */
const CellStack = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  min-width: 84px;
`;

const BarTrack = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  width: ${(p) => Math.max(3, Math.min(100, p.$pct || 0))}%;
  background: ${(p) => p.$color || "var(--accent)"};
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
`;

const Notice = styled.div`
  border: 1px dashed var(--line);
  border-radius: 12px;
  padding: 22px;
  text-align: center;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.6;

  code {
    font-family: "JetBrains Mono", monospace;
    background: var(--ink);
    border: 1px solid var(--line);
    color: var(--accent);
    padding: 1px 6px;
    border-radius: 5px;
  }
`;

const num = (n) =>
  n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });

const PublicDemo = () => {
  const [phData, setPhData] = useState([]);
  const [phLoading, setPhLoading] = useState(true);
  const [phError, setPhError] = useState(null);
  const [phFetchedAt, setPhFetchedAt] = useState(null);
  const [tableSearch, setTableSearch] = useState("");

  // Global filters — apply to KPIs, insight, both charts, and the table.
  // Kept as committed value + separate text input so the dropdown can show
  // suggestions while typing without filtering on every keystroke until
  // a suggestion (or exact value) is actually chosen/cleared.
  const [countryFilter, setCountryFilter] = useState(null);
  const [countryInput, setCountryInput] = useState("");
  const [moduleFilter, setModuleFilter] = useState([]);

  const chartRef = useRef(null);
  const pieChartRef = useRef(null);

  const fetchPosthog = useCallback(async () => {
    if (!POSTHOG_QUERY_KEY) {
      setPhError("no-key");
      setPhLoading(false);
      return;
    }
    setPhLoading(true);
    setPhError(null);
    try {
      const res = await fetch(
        `${POSTHOG_HOST}/api/projects/${POSTHOG_PROJECT_ID}/query/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${POSTHOG_QUERY_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: { kind: "HogQLQuery", query: POSTHOG_HOGQL },
            refresh: "force_blocking",
          }),
        }
      );
      if (!res.ok) throw new Error(`PostHog API ${res.status}`);
      const json = await res.json();
      const cols = json.columns || [];
      const rows = (json.results || []).map((r, i) => {
        const o = { _k: i };
        cols.forEach((c, idx) => {
          o[c] = r[idx];
        });
        return o;
      });
      setPhData(rows);
      setPhFetchedAt(new Date());
    } catch (e) {
      setPhError(e.message || "Failed to fetch PostHog data");
    } finally {
      setPhLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosthog();
  }, [fetchPosthog]);

  /* ── Searchable suggestion lists for the two filters ──
     Sorted alphabetically so the dropdown reads as a stable, scannable list
     rather than shifting order on every fetch. These stay full master lists
     (not narrowed by the other filter) so a user can always switch either
     filter to anything that ever appeared in the data. */
  const countryOptions = useMemo(() => {
    const set = new Set(phData.map((d) => d.country).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [phData]);

  const moduleOptions = useMemo(() => {
    const set = new Set(phData.map((d) => d.module).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [phData]);

  /* live suggestions for each AutoComplete — narrowed by what's typed so far,
     AND cross-scoped by the other committed filter, so picking a country
     surfaces only modules that actually occur there (and vice versa).
     Important: once a value is selected, the input text equals that value,
     so re-opening the dropdown (e.g. clicking back into the field) must NOT
     re-filter the pool down to "just the thing that's already selected" —
     that's treated the same as an empty query, showing the full pool again. */
  const countrySuggestions = useMemo(() => {
    const typedQuery = countryInput.trim().toLowerCase();
    const isUnchangedSelection = countryFilter && countryInput === countryFilter;
    const q = isUnchangedSelection ? "" : typedQuery;
    const pool = moduleFilter.length
      ? Array.from(new Set(phData.filter((d) => moduleFilter.includes(d.module)).map((d) => d.country))).sort((a, b) =>
          a.localeCompare(b)
        )
      : countryOptions;
    const list = q ? pool.filter((c) => c.toLowerCase().includes(q)) : pool;
    return list.map((c) => ({ value: c }));
  }, [countryOptions, countryInput, countryFilter, moduleFilter, phData]);

  /* ── Global filter — applied once, upstream of every KPI/chart/table so
     they all stay in sync. Country/module filters are exact-match (chosen
     from suggestions), independent of the free-text table search below,
     which still narrows further within whatever the filters already show. */
  const filteredData = useMemo(() => {
    if (!countryFilter && !moduleFilter) return phData;
    return phData.filter(
      (d) =>
        (!countryFilter || d.country === countryFilter) &&
        (!moduleFilter.length || moduleFilter.includes(d.module))
    );
  }, [phData, countryFilter, moduleFilter]);

  const hasActiveFilters = Boolean(countryFilter || moduleFilter.length);

  const clearAllFilters = () => {
    setCountryFilter(null);
    setCountryInput("");
    setModuleFilter([]);
  };

  /* ── Analyst summary ── */
  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    const countries = new Set(filteredData.map((d) => d.country)).size;
    const modules = new Set(filteredData.map((d) => d.module)).size;
    const totalPageviews = filteredData.reduce((s, d) => s + (d.pageviews || 0), 0);
    const totalMinutes = filteredData.reduce((s, d) => s + (d.total_minutes || 0), 0);
    const avgAdoption =
      filteredData.reduce((s, d) => s + (d.adoption_rate_pct || 0), 0) / filteredData.length;
    const top = [...filteredData].sort(
      (a, b) => (b.total_minutes || 0) - (a.total_minutes || 0)
    )[0];
    return {
      countries,
      modules,
      totalPageviews,
      totalMinutes: Math.round(totalMinutes * 10) / 10,
      avgAdoption: Math.round(avgAdoption),
      top,
    };
  }, [filteredData]);

  /* smallest country sample size currently in view — adoption % is unreliable below this */
  const minSample = useMemo(() => {
    if (!filteredData.length) return null;
    return Math.min(...filteredData.map((d) => d.country_total_users ?? d.users ?? 0));
  }, [filteredData]);

  /* share of total minutes per module, for the "Where Time Goes" donut.
     Capped to top N + "Other" so a long tail of modules doesn't turn the
     legend into an unreadable wall of text. */
  const moduleShare = useMemo(() => {
    const totals = new Map();
    filteredData.forEach((d) => {
      totals.set(d.module, (totals.get(d.module) || 0) + (d.total_minutes || 0));
    });
    const sorted = Array.from(totals, ([name, value]) => ({
      name,
      value: Math.round(value * 10) / 10,
    })).sort((a, b) => b.value - a.value);
    if (sorted.length <= CHART_TOP_N) return sorted;
    const top = sorted.slice(0, CHART_TOP_N);
    const otherTotal = sorted.slice(CHART_TOP_N).reduce((s, m) => s + m.value, 0);
    return [...top, { name: `Other (${sorted.length - CHART_TOP_N})`, value: Math.round(otherTotal * 10) / 10 }];
  }, [filteredData]);

  /* all rows for the "Module Usage" bar chart, sorted so the most relevant
     ones lead — capped only at a hard safety ceiling, not silently truncated
     to the top N. The chart itself uses dataZoom to default to showing the
     top N while staying scrollable/zoomable to reach everything else. */
  const chartData = useMemo(
    () => [...filteredData].sort((a, b) => (b.total_minutes || 0) - (a.total_minutes || 0)).slice(0, CHART_SAFETY_CAP),
    [filteredData]
  );

  const maxPageviews = useMemo(
    () => Math.max(1, ...filteredData.map((d) => d.pageviews || 0)),
    [filteredData]
  );
  const maxMinutes = useMemo(
    () => Math.max(1, ...filteredData.map((d) => d.total_minutes || 0)),
    [filteredData]
  );

  /* table search — further narrows by country or module text within
     whatever the country/module filters above already show, so a large
     dataset can be navigated directly instead of paging through it blind */
  const tableData = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return filteredData;
    return filteredData.filter(
      (d) => (d.country || "").toLowerCase().includes(q) || (d.module || "").toLowerCase().includes(q)
    );
  }, [filteredData, tableSearch]);

  const phColumns = [
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      render: (c) => <Pill>{c}</Pill>,
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      render: (m) => <Mono>{m}</Mono>,
    },
    { title: "Users", dataIndex: "users", key: "users", align: "right", render: (v) => <Mono>{num(v)}</Mono> },
    {
      title: "Pageviews",
      dataIndex: "pageviews",
      key: "pageviews",
      align: "right",
      render: (v) => (
        <CellStack>
          <Mono>{num(v)}</Mono>
          <BarTrack>
            <BarFill $pct={(v / maxPageviews) * 100} $color="#c6f24e" />
          </BarTrack>
        </CellStack>
      ),
    },
    {
      title: "Total Min",
      dataIndex: "total_minutes",
      key: "total_minutes",
      align: "right",
      render: (v) => (
        <CellStack>
          <Mono>{num(v)}</Mono>
          <BarTrack>
            <BarFill $pct={(v / maxMinutes) * 100} $color="#ffb454" />
          </BarTrack>
        </CellStack>
      ),
    },
    { title: "Avg s/View", dataIndex: "avg_seconds_per_view", key: "avg", align: "right", render: (v) => <Mono>{v == null ? "—" : num(v)}</Mono> },
    {
      title: "Adoption",
      dataIndex: "adoption_rate_pct",
      key: "adoption_rate_pct",
      align: "right",
      render: (v, record) => {
        const sample = record.country_total_users ?? record.users ?? 0;
        const lowSample = sample < MIN_SAMPLE_USERS;
        return (
          <CellStack>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {lowSample && (
                <WarningOutlined
                  style={{ color: "#ffb454", fontSize: 11 }}
                  title={`Based on ${sample} user${sample === 1 ? "" : "s"} — below the ${MIN_SAMPLE_USERS}-user confidence threshold`}
                />
              )}
              <Pill $accent={!lowSample} $warn={lowSample}>{v}%</Pill>
            </div>
            <BarTrack>
              <BarFill $pct={v} $color={lowSample ? "#ffb454" : "#5ee0c8"} />
            </BarTrack>
          </CellStack>
        );
      },
    },
  ];

  const axisColor = "#868d99";
  const chartOption = {
    backgroundColor: "transparent",
    textStyle: { color: "#c7ced9", fontFamily: "JetBrains Mono, monospace" },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "#141821",
      borderColor: "#20242e",
      textStyle: { color: "#eef1f4" },
    },
    legend: {
      type: "scroll",
      data: ["Pageviews", "Users", "Total minutes"],
      top: 0,
      textStyle: { color: "#c7ced9" },
      pageIconColor: "#c6f24e",
      pageIconInactiveColor: "#3a3f4b",
      pageTextStyle: { color: "#868d99" },
    },
    grid: { left: 8, right: 16, bottom: chartData.length > CHART_TOP_N ? 64 : 8, top: 46, containLabel: true },
    // dataZoom defaults to showing the top N (already sorted by total minutes)
    // but stays draggable/scrollable/zoomable so every row is reachable —
    // nothing is silently hidden, just collapsed behind a scrollbar.
    dataZoom:
      chartData.length > CHART_TOP_N
        ? [
            { type: "inside", start: 0, end: Math.min(100, (CHART_TOP_N / chartData.length) * 100) },
            {
              type: "slider",
              start: 0,
              end: Math.min(100, (CHART_TOP_N / chartData.length) * 100),
              height: 18,
              bottom: 6,
              backgroundColor: "#141821",
              borderColor: "#20242e",
              fillerColor: "rgba(198, 242, 78, 0.15)",
              handleStyle: { color: "#c6f24e", borderColor: "#0e1116" },
              textStyle: { color: "#868d99" },
              moveHandleStyle: { color: "#20242e" },
            },
          ]
        : [],
    xAxis: {
      type: "category",
      data: chartData.map((d) => `${d.country} · ${d.module}`),
      axisLabel: { color: axisColor, rotate: chartData.length > 4 ? 35 : 0, interval: 0 },
      axisLine: { lineStyle: { color: "#20242e" } },
    },
    yAxis: {
      type: "value",
      axisLabel: { color: axisColor },
      splitLine: { lineStyle: { color: "#1a1e27" } },
    },
    series: [
      { name: "Pageviews", type: "bar", data: chartData.map((d) => d.pageviews), itemStyle: { color: "#c6f24e", borderRadius: [4, 4, 0, 0] } },
      { name: "Users", type: "bar", data: chartData.map((d) => d.users), itemStyle: { color: "#5ee0c8", borderRadius: [4, 4, 0, 0] } },
      { name: "Total minutes", type: "bar", data: chartData.map((d) => d.total_minutes), itemStyle: { color: "#ffb454", borderRadius: [4, 4, 0, 0] } },
    ],
  };

  const pieColors = ["#c6f24e", "#5ee0c8", "#ffb454", "#ff6b81", "#7aa2ff", "#c792ea", "#f6c177"];
  const pieOption = {
    backgroundColor: "transparent",
    textStyle: { color: "#c7ced9", fontFamily: "JetBrains Mono, monospace" },
    tooltip: {
      trigger: "item",
      backgroundColor: "#141821",
      borderColor: "#20242e",
      textStyle: { color: "#eef1f4" },
      formatter: "{b}: {c} min ({d}%)",
    },
    legend: {
      type: "scroll",
      orient: "vertical",
      right: 4,
      top: 20,
      bottom: 20,
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: "#c7ced9", fontSize: 11.5 },
      pageIconColor: "#c6f24e",
      pageIconInactiveColor: "#3a3f4b",
      pageTextStyle: { color: "#868d99" },
    },
    series: [
      {
        name: "Time share",
        type: "pie",
        radius: ["46%", "72%"],
        center: ["36%", "50%"],
        itemStyle: { borderColor: "#0e1116", borderWidth: 2, borderRadius: 6 },
        label: { show: false },
        labelLine: { show: false },
        emphasis: {
          label: { show: true, fontSize: 13, fontWeight: 600, color: "#eef1f4", formatter: "{b}\n{d}%" },
        },
        data: moduleShare.map((m, i) => ({ ...m, itemStyle: { color: pieColors[i % pieColors.length] } })),
      },
    ],
  };

  /* ── Exports — both reflect the active country/module filters, so the
     file matches what's currently on screen rather than always the full set. */
  const exportExcel = () => {
    if (!filteredData.length) return;
    const rows = filteredData.map(({ _k, ...rest }) => rest);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Module Adoption");
    XLSX.writeFile(wb, `module-adoption-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPDF = () => {
    if (!filteredData.length) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Module Adoption by Country", 40, 46);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(120);
    const filterNote = hasActiveFilters
      ? `  ·  Filtered: ${countryFilter ? `country=${countryFilter}` : ""}${
          countryFilter && moduleFilter.length ? ", " : ""
        }${moduleFilter.length ? `module=${moduleFilter.join(", ")}` : ""}`
      : "";
    doc.text(
      `Generated ${new Date().toLocaleString()}  ·  Source: PostHog (live)${filterNote}`,
      40,
      64
    );

    if (stats) {
      doc.setTextColor(30);
      doc.setFontSize(11);
      doc.text(
        `Summary: ${stats.top.country} leads on "${stats.top.module}" (${stats.top.total_minutes} min). ` +
          `${stats.totalPageviews} pageviews across ${stats.modules} modules in ${stats.countries} ` +
          `${stats.countries === 1 ? "country" : "countries"}; avg adoption ${stats.avgAdoption}%.`,
        40,
        86,
        { maxWidth: 760 }
      );
    }

    let y = 104;
    try {
      const barInst = chartRef.current?.getEchartsInstance?.();
      const pieInst = pieChartRef.current?.getEchartsInstance?.();
      const chartH = 210;
      if (barInst) {
        const img = barInst.getDataURL({ pixelRatio: 2, backgroundColor: "#0e1116" });
        doc.addImage(img, "PNG", 40, y, 490, chartH);
      }
      if (pieInst) {
        const img = pieInst.getDataURL({ pixelRatio: 2, backgroundColor: "#0e1116" });
        doc.addImage(img, "PNG", 540, y, 260, chartH);
      }
      if (barInst || pieInst) y += chartH + 24;
    } catch (_) {
      /* charts optional in PDF */
    }

    const lowSample = (d) => (d.country_total_users ?? d.users ?? 0) < MIN_SAMPLE_USERS;

    autoTable(doc, {
      startY: y,
      head: [["Country", "Module", "Users", "Sample (n)", "Pageviews", "Total Min", "Avg s/View", "Adoption %"]],
      body: filteredData.map((d) => [
        d.country,
        d.module,
        d.users,
        d.country_total_users ?? "—",
        d.pageviews,
        d.total_minutes,
        d.avg_seconds_per_view ?? "—",
        `${d.adoption_rate_pct}%${lowSample(d) ? " (low sample)" : ""}`,
      ]),
      styles: { fontSize: 9, cellPadding: 5 },
      headStyles: { fillColor: [14, 17, 22], textColor: [198, 242, 78] },
      alternateRowStyles: { fillColor: [244, 246, 240] },
    });

    doc.save(`module-adoption-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const hasData = filteredData.length > 0;

  return (
    <Page>
      <Fonts />
      <RootVars />
      <TableTheme />
      <Inner>
        {/* Hero */}
        <Reveal>
          <TopBar>
            <div>
              <Eyebrow>PostHog · Live Telemetry</Eyebrow>
              <Headline>
                Module Adoption<br />
                by <em>Country</em>
              </Headline>
              <Sub>
                Where attention actually goes — which modules each country uses,
                how long they stay, and how widely it's adopted. No login, fetched
                live.
              </Sub>
            </div>
            <Actions>
              <Btn onClick={fetchPosthog} disabled={phLoading} title="Refresh">
                <ReloadOutlined spin={phLoading} /> Refresh
              </Btn>
              <Btn onClick={exportExcel} disabled={!hasData} title="Download Excel">
                <FileExcelOutlined /> Excel
              </Btn>
              <Btn $primary onClick={exportPDF} disabled={!hasData} title="Download PDF">
                <FilePdfOutlined /> PDF
              </Btn>
            </Actions>
          </TopBar>
        </Reveal>

        {/* Global filters — searchable, with suggestions, applied everywhere below */}
        <Reveal style={{ animationDelay: "0.05s" }}>
          <FilterBar>
            <FilterField>
              <FilterLabel htmlFor="country-filter">
                <GlobalOutlined className="ico" /> Country
              </FilterLabel>
              <SearchableField>
                <SearchOutlined className="search-ico" />
                <AutoComplete
                  id="country-filter"
                  value={countryInput}
                  options={countrySuggestions}
                  popupClassName="filter-autocomplete-dropdown"
                  dropdownClassName="filter-autocomplete-dropdown"
                  dropdownStyle={{
                    background: "#141821",
                    border: "1px solid #20242e",
                    boxShadow: "0 12px 28px rgba(0, 0, 0, 0.45)",
                  }}
                  onSearch={(v) => setCountryInput(v)}
                  onSelect={(v) => {
                    setCountryFilter(v);
                    setCountryInput(v);
                  }}
                  onChange={(v) => {
                    setCountryInput(v);
                    if (!v) setCountryFilter(null);
                  }}
                  onBlur={() => {
                    // typed text that doesn't match any known country shouldn't
                    // silently filter to zero rows — only commit exact matches
                    if (countryInput && !countryOptions.includes(countryInput)) {
                      setCountryInput(countryFilter || "");
                    }
                  }}
                  filterOption={false}
                  allowClear
                  onClear={() => {
                    setCountryFilter(null);
                    setCountryInput("");
                  }}
                  disabled={phData.length === 0}
                  style={{ width: "100%" }}
                  placeholder={
                    countryOptions.length
                      ? `Search ${countryOptions.length} countr${countryOptions.length === 1 ? "y" : "ies"}…`
                      : "Search country…"
                  }
                />
              </SearchableField>
            </FilterField>

            <FilterField>
              <FilterLabel htmlFor="module-filter">
                <AppstoreOutlined className="ico" /> Module
              </FilterLabel>
              <Select
                id="module-filter"
                mode="multiple"
                allowClear
                showSearch
                value={moduleFilter}
                onChange={(vals) => setModuleFilter(vals)}
                onClear={() => setModuleFilter([])}
                disabled={phData.length === 0}
                style={{ width: "100%" }}
                placeholder={
                  moduleOptions.length
                    ? `Search ${moduleOptions.length} modules…`
                    : "Search module…"
                }
                options={moduleOptions.map((m) => ({ value: m, label: m }))}
                popupClassName="filter-autocomplete-dropdown"
                maxTagCount="responsive"
              />
            </FilterField>

            <ClearAllBtn onClick={clearAllFilters} disabled={!hasActiveFilters}>
              <CloseCircleOutlined /> Clear filters
            </ClearAllBtn>
          </FilterBar>
        </Reveal>

        {hasActiveFilters && (
          <ActiveFilters>
            {countryFilter && (
              <FilterChip>
                {countryFilter}
                <span
                  className="x"
                  onClick={() => {
                    setCountryFilter(null);
                    setCountryInput("");
                  }}
                >
                  <CloseCircleOutlined />
                </span>
              </FilterChip>
            )}
            {moduleFilter.map((mod) => (
              <FilterChip key={mod}>
                {mod}
                <span
                  className="x"
                  onClick={() => setModuleFilter((prev) => prev.filter((m) => m !== mod))}
                >
                  <CloseCircleOutlined />
                </span>
              </FilterChip>
            ))}
          </ActiveFilters>
        )}

        {/* KPIs */}
        <Reveal style={{ animationDelay: "0.08s" }}>
          <KpiGrid>
            <Kpi>
              <div className="label">Countries</div>
              <div className="value">{stats ? stats.countries : "—"}</div>
              <div className="foot">active regions tracked</div>
            </Kpi>
            <Kpi>
              <div className="label">Modules</div>
              <div className="value">{stats ? stats.modules : "—"}</div>
              <div className="foot">distinct app areas used</div>
            </Kpi>
            <Kpi>
              <div className="label">Pageviews</div>
              <div className="value">{stats ? num(stats.totalPageviews) : "—"}</div>
              <div className="foot">total across all modules</div>
            </Kpi>
            <Kpi>
              <div className="label">Minutes</div>
              <div className="value">{stats ? num(stats.totalMinutes) : "—"}</div>
              <div className="foot">total attention time</div>
            </Kpi>
            <Kpi>
              <div className="label">Avg Adoption</div>
              <div className="value">{stats ? `${stats.avgAdoption}%` : "—"}</div>
              <div className="foot">mean across modules</div>
            </Kpi>
          </KpiGrid>
        </Reveal>

        {/* Plain-English insight */}
        <Reveal style={{ animationDelay: "0.16s" }}>
          <Insight>
            <RiseOutlined className="ico" />
            <div>
              {stats ? (
                <>
                  <b>{stats.top.country}</b> leads engagement — most time is spent
                  on <b>{stats.top.module}</b> ({num(stats.top.total_minutes)} min).
                  Overall, <b>{num(stats.totalPageviews)}</b> pageviews were tracked
                  across <b>{stats.modules}</b> modules in{" "}
                  <b>{stats.countries}</b>{" "}
                  {stats.countries === 1 ? "country" : "countries"}, with an average
                  adoption of <b>{stats.avgAdoption}%</b>.
                </>
              ) : hasActiveFilters ? (
                "No rows match the current country/module filters — try clearing one of them."
              ) : (
                "Live insight will appear here once data loads — it explains, in plain English, who's most engaged and where."
              )}
              {stats && minSample != null && minSample < MIN_SAMPLE_USERS && (
                <Caveat>
                  <WarningOutlined />
                  Based on a small sample ({minSample} user{minSample === 1 ? "" : "s"}) —
                  treat percentages as directional, not conclusive, until more data comes in.
                </Caveat>
              )}
            </div>
          </Insight>
        </Reveal>

        {/* Charts — hidden while genuinely empty (loading/error), replaced by
            a single explanatory panel when filters are the reason nothing shows */}
        {!hasData && hasActiveFilters && phData.length > 0 && (
          <Reveal style={{ animationDelay: "0.22s" }}>
            <Panel>
              <PanelHead>
                <span className="t">Module Usage &amp; Time Share</span>
                <span className="m">no rows in current filter</span>
              </PanelHead>
              <Notice>
                <strong>{countryFilter || "Any country"}</strong>
                {countryFilter && moduleFilter.length ? " + " : ""}
                {moduleFilter.length ? <strong>{moduleFilter.join(", ")}</strong> : ""} has no
                tracked activity yet. Try a different combination.{" "}
                <Btn style={{ marginTop: 12 }} onClick={clearAllFilters}>
                  Clear filters
                </Btn>
              </Notice>
            </Panel>
          </Reveal>
        )}

        {/* Chart */}
        {hasData && (
          <Reveal style={{ animationDelay: "0.22s" }}>
            <Panel>
              <PanelHead>
                <span className="t">Module Usage</span>
                <span className="m">
                  pageviews · users · minutes
                  {chartData.length > CHART_TOP_N
                    ? ` · showing top ${CHART_TOP_N} of ${chartData.length} — drag the scrollbar below for the rest`
                    : ""}
                </span>
              </PanelHead>
              <ReactECharts
                ref={chartRef}
                option={chartOption}
                style={{ height: chartData.length > CHART_TOP_N ? 400 : 360 }}
                notMerge
              />
            </Panel>
          </Reveal>
        )}

        {/* Where time goes — donut, so proportion reads at a glance */}
        {hasData && (
          <Reveal style={{ animationDelay: "0.25s" }}>
            <Panel>
              <PanelHead>
                <span className="t">Where Time Goes</span>
                <span className="m">
                  share of total minutes, by module
                  {moduleShare.length && moduleShare[moduleShare.length - 1].name.startsWith("Other (")
                    ? ` · top ${CHART_TOP_N}, rest grouped`
                    : ""}
                </span>
              </PanelHead>
              <ReactECharts
                ref={pieChartRef}
                option={pieOption}
                style={{ height: 280 }}
                notMerge
              />
            </Panel>
          </Reveal>
        )}

        {/* Table */}
        <Reveal style={{ animationDelay: "0.28s" }}>
          <Panel>
            <PanelHead>
              <span className="t">Detail Breakdown</span>
              <span className="m">
                {phFetchedAt
                  ? `live · ${phFetchedAt.toLocaleTimeString()}`
                  : "awaiting data"}
                {tableSearch.trim() ? ` · ${tableData.length} of ${filteredData.length} match` : ""}
              </span>
            </PanelHead>

            {phError === "no-key" ? (
              <Notice>
                Set a <strong>read-only</strong> PostHog key{" "}
                (<code>query:read</code>) in <code>public/index.html</code> as{" "}
                <code>window.__POSTHOG_QUERY_KEY__ = "phx_..."</code> to load live
                data. Never use the full-access key here.
              </Notice>
            ) : phError ? (
              <Notice>
                Could not load data: {phError}.{" "}
                <Btn style={{ marginTop: 12 }} onClick={fetchPosthog}>
                  Retry
                </Btn>
              </Notice>
            ) : !hasData && hasActiveFilters ? (
              <Notice>
                No rows match <strong>{countryFilter || "any country"}</strong>
                {countryFilter && moduleFilter.length ? " + " : ""}
                {moduleFilter.length ? <strong>{moduleFilter.join(", ")}</strong> : ""}.{" "}
                <Btn style={{ marginTop: 12 }} onClick={clearAllFilters}>
                  Clear filters
                </Btn>
              </Notice>
            ) : (
              <>
                {phData.length > 0 && (
                  <Input
                    allowClear
                    placeholder="Search by country or module…"
                    prefix={<SearchOutlined style={{ color: "var(--muted)" }} />}
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    style={{ maxWidth: 320, marginBottom: 14 }}
                  />
                )}
                <Spin spinning={phLoading} tip="Fetching live PostHog data…">
                  <Table
                    key={tableSearch}
                    rowKey="_k"
                    columns={phColumns}
                    dataSource={tableData}
                    pagination={
                      tableData.length > TABLE_PAGE_SIZE
                        ? {
                            pageSize: TABLE_PAGE_SIZE,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 50, 100],
                            showTotal: (total) => `${total.toLocaleString()} rows`,
                          }
                        : false
                    }
                    scroll={{ x: 760, y: tableData.length > TABLE_PAGE_SIZE ? 480 : undefined }}
                    locale={{ emptyText: tableSearch.trim() ? "No rows match your search" : "No data" }}
                  />
                </Spin>
              </>
            )}
          </Panel>
        </Reveal>
      </Inner>
    </Page>
  );
};

export default PublicDemo;